import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPreferredPrinterConfig } from '../../utils/printerPreferences';

const CashierQuickActions = ({
  context = 'general',
  quickPickCategories = [],
  quickPickProducts = [],
  onQuickPick,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const canViewCheckout = useMemo(() => user?.permissions?.includes('sales:create'), [user?.permissions]);
  const canViewReturns = useMemo(() => user?.permissions?.includes('refunds:create'), [user?.permissions]);
  const canViewTills = useMemo(() => user?.permissions?.includes('tills:manage'), [user?.permissions]);
  const canViewCustomers = useMemo(() => user?.permissions?.includes('customers:read'), [user?.permissions]);
  const showOpenTillButton = context === 'checkout' && canViewTills;

  const handleOpenTillDrawer = async () => {
    try {
      const plugin = window?.Capacitor?.Plugins?.ReceiptPrinter;
      if (!plugin) {
        console.debug('[CashierQuickActions] ReceiptPrinter plugin unavailable.');
        return;
      }
      const payload = { jobType: 'cash' };
      const cfg = getPreferredPrinterConfig('receipt');
      if (cfg.transport === 'network' && cfg.host) {
        payload.networkHost = cfg.host;
        if (cfg.port) payload.networkPort = cfg.port;
      } else if (cfg.bluetoothName) {
        payload.bluetoothNameHint = cfg.bluetoothName;
      }
      await plugin.openDrawer(payload);
    } catch (err) {
      console.warn('[CashierQuickActions] Failed to open drawer:', err);
    }
  };

  const showQuickPick =
    context === 'checkout'
    && (quickPickCategories.length > 0 || quickPickProducts.length > 0);
  const shouldShowBar =
    showQuickPick ||
    canViewCheckout ||
    canViewReturns ||
    canViewTills ||
    canViewCustomers ||
    showOpenTillButton;

  if (!shouldShowBar) return null;

  return (
    <div className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
      <div className={`swan-main-shell py-3 flex items-center gap-2 ${showQuickPick ? 'justify-between' : 'justify-end'}`}>
        {showQuickPick && (
          <div className="flex items-center gap-2 flex-wrap">
            {quickPickCategories.map((category) => (
              <button
                key={category._id}
                onClick={() => onQuickPick?.(category)}
                className="px-3 py-2 text-sm font-semibold rounded-full border border-primary text-primary hover:bg-primary hover:text-white"
              >
                {category.name}
              </button>
            ))}
            {quickPickProducts.map((entry) => (
              <button
                key={entry.key}
                onClick={() => onQuickPick?.({ product: entry.product })}
                className="px-3 py-2 text-sm font-semibold rounded-full border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
              >
                {entry.product?.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap justify-end">
        {canViewCheckout && (
          <button
            onClick={() => navigate('/checkout')}
            className="px-3 py-2 text-sm font-semibold rounded-full border border-slate-300 text-slate-700 hover:border-primary hover:text-primary"
          >
            Checkout
          </button>
        )}
        {canViewReturns && (
          <button
            onClick={() => navigate('/returns')}
            className="px-3 py-2 text-sm font-semibold rounded-full border border-slate-300 text-slate-700 hover:border-primary hover:text-primary"
          >
            Returns
          </button>
        )}
        {canViewTills && (
          <button
            onClick={() => navigate('/tills')}
            className="px-3 py-2 text-sm font-semibold rounded-full border border-slate-300 text-slate-700 hover:border-primary hover:text-primary"
          >
            Tills
          </button>
        )}
        {canViewCustomers && (
          <button
            onClick={() => navigate('/customers')}
            className="px-3 py-2 text-sm font-semibold rounded-full border border-slate-300 text-slate-700 hover:border-primary hover:text-primary"
          >
            Customers
          </button>
        )}
        {showOpenTillButton && (
          <button
            onClick={handleOpenTillDrawer}
            className="px-3 py-2 text-sm font-semibold rounded-full border border-primary text-primary hover:bg-primary hover:text-white"
          >
            Open Till
          </button>
        )}
        <button
          onClick={() => logout()}
          className="px-3 py-2 text-sm font-semibold rounded-full border border-rose-200 text-rose-600 hover:border-rose-400"
        >
          Logout
        </button>
        </div>
      </div>
    </div>
  );
};

export default CashierQuickActions;
